const INVALID_CLASS = 'invalid';
const CONTROL_SELECTOR = '.form-control';
const MESSAGE_SELECTOR = '.error-message';
const ICON_SELECTOR = '.error-icon';

// TODO: Might be nice to add multiple different messages for different validations.
export type ValidationAction = ((value: string, formData: Record<string, string>) => boolean);
export interface Validation {
    message: string;
    pattern?: RegExp;
    action?: ValidationAction;
}
export interface Validator {
    required?: boolean;
    message?: string;
    validations?: Validation[];
}
export type Validations = Record<string, Validator>;

/**
 * Clears any input, textarea and checkbox from any values
 */
export function clearForm(form: JQuery | HTMLFormElement): void {
    const $form = $(form);
    const $inputs = $form.find('input, textarea');
    const $checkboxes = $form.find('input[type=checkbox]');

    $inputs.val('');
    $checkboxes.prop('checked', false);
}

/**
 * Serializes a form input data into an object with the input names mapped to their values
 */
export function formData($form: JQuery | Element): Record<string, string> {
    return $($form).serializeArray().reduce((obj, item) => {
        obj[item.name] = item.value;

        return obj;
    }, Object.create(null));
}

/**
 * Validates a form control
 */
function validateControl(formData: Record<string, string>, key: string, validator: Validator): string | null {
    const { required, validations, message } = validator;
    const value = formData[key];

    // if required use default message
    if (required && !value) {
        return message ?? null;
    }

    return validations?.find(validation => {
        const { pattern, action } = validation;

        // if regex use regex test
        // if function run function
        if (pattern && !pattern.test(value)) {
            return true;
        }

        if (typeof action == 'function' && !action(value, formData)) {
            return true;
        }

        return false;
    })?.message ?? null;
}

/**
 * Shows/hides error on form element
 */
function toggleFormElementValidation($el: (Element | RadioNodeList | JQuery<Element>), errorMessage: string | null): void {
    const $input = $($el);
    const $control = $input.closest(CONTROL_SELECTOR);
    const $message = $control.find(MESSAGE_SELECTOR);
    const $icon = $control.find(ICON_SELECTOR);
    const isValid = !errorMessage;

    // Validation error adds warning icon and warning text
    if (!isValid) {
        $message.text('' + errorMessage);

        const $errorId = $input.prop('name') + '-error';

        $message.prop('id', $errorId);
        $input
            .attr('aria-describedby', $errorId)
            .addClass(INVALID_CLASS);
    } else {
        $message.prop('id', false);
        $message.empty();
        $input
            .removeAttr('aria-describedby')
            .removeClass(INVALID_CLASS);
    }

    // Padding fix for icon
    if ($input.prop('type') == 'text' || $input.is('textarea')) {
        $input.toggleClass('pr-10', !isValid);
    }

    // Hide/show message on success/fail
    $message.toggleClass('hidden', isValid);
    $icon.toggleClass('hidden', isValid);
}

/**
 * Validate a form using specified validations
 */
export function validateForm(form: HTMLFormElement, validations: Validations, formData: Record<string, string>): boolean {
    let success = true;


    Object.keys(validations).forEach((key) => {
        // No validation for this element
        let errorMessage: string|null = null;

        try {
            const validator = validations[key];
            errorMessage = validateControl(formData, key, validator);
        } catch (ex) {
            errorMessage = 'Valideringsfel i fält!';
        }

        if (errorMessage != null) {
            success = false;
        }

        // Show/hide error for form control
        toggleFormElementValidation(form.elements[key], errorMessage);
    });

    return success;
}

/**
 * Binds necessary events to form controls
 */
export function bindFormValidationEvents(form: HTMLFormElement, submitHandler?: (e: Event) => any) {
    const $inputs: JQuery<Element>[] = [];

    for (let i = 0; i < form.elements.length; i++) {
        const $input = $(form.elements[i]);
        const $control = $input.closest(CONTROL_SELECTOR);

        if ($control.length) {
            $inputs.push($input);
            const tagName = $input.prop('tagName')?.toLowerCase() ?? '';
            const type = $input.prop('type')?.toLowerCase() ?? '';

            if (tagName == 'input' && (type == 'radio' || type == 'checkbox')) {
                $input.on('change', () => toggleFormElementValidation($input, null));
            } else {
                $input.on('input', () => toggleFormElementValidation($input, null));
            }
        }
    }

    // Clear validations on submit
    form.addEventListener('submit', (e) => {
        e.preventDefault();

        // Reset validation before running submit handler
        $inputs.forEach(x => toggleFormElementValidation(x, null));

        // If custom submit handler was provided run this beforehand
        if (typeof submitHandler == 'function') {
            submitHandler(e);
        }
    }, false);
}


export interface AjaxFormOptions<T> {
    form: HTMLFormElement;
    type: string;
    url: string;
    data: any;
    process?(response: T): boolean;
    done?(response: T): void;
    error?(statusText: string): string;
}


export function sendForm<TRes>(opts: AjaxFormOptions<TRes>) {
    const $form = opts.form;
    const $error = $form.querySelector('.form-error');
    const $success = $form.querySelector('.form-success');
    const defaultError = 'Formuläret kunde inte skickas, kontakta support.';

    // initially hide status texts
    $error?.classList.add('hidden');
    $success?.classList.add('hidden');

    function onSubmitSuccess(res: TRes): void {
        // Show success message
        $error?.classList.add('hidden');
        $success?.classList.remove('hidden');

        clearForm($form);

        if (typeof opts.done == 'function') {
            opts.done(res);
        }
    }

    function onSubmitError(status: string): void {
        // Show error message
        $success?.classList.add('hidden');

        if ($error != null) {
            let message = defaultError;

            if (typeof opts.error == 'function') {
                message = opts.error(status);
            }

            $error.classList.remove('hidden');
            $error.textContent = message;
        }
    }

    $.ajax({
        type: opts.type,
        url: opts.url,
        data: JSON.stringify(opts.data),
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
        success: (res, status) => {
            let failed = false;

            if (typeof opts.process == 'function') {
                failed = !opts.process(res);
            }

            if (!failed) {
                onSubmitSuccess(res);
            } else {
                onSubmitError(status);
            }
        },
        error: (_, status) => onSubmitError(status),
    });
}
